import {
  MOCK_POLICIES,
  MOCK_POLICY_DETAILS,
  findPolicyDetailById,
} from '../mock-data/policies.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  Permission,
  PermissionAccess,
  PermissionInput,
  PolicyCreateApiRequest,
  PolicyDetailApiResponse,
  PolicyListApiResponse,
  PolicyUpdateApiRequest,
} from '../../features/policies/policies.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /policies* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス（例: /policies, /policies/:id）
 */
export function handlePoliciesRequest(
  method: string,
  path: string,
  body: unknown,
): Observable<HttpResponse<unknown>> | null {
  // GET /policies - ポリシー一覧取得
  if (method === 'GET' && path === '/policies') {
    return handleGetPolicies();
  }

  // POST /policies - ポリシー作成
  if (method === 'POST' && path === '/policies') {
    return handleCreatePolicy(body as PolicyCreateApiRequest);
  }

  // GET /policies/:id - ポリシー詳細取得
  const getPolicyDetailMatch = /^\/policies\/([^/]+)$/.exec(path);
  if (method === 'GET' && getPolicyDetailMatch) {
    const policyId = getPolicyDetailMatch[1];
    return handleGetPolicyDetail(policyId);
  }

  // PUT /policies/:id - ポリシー更新
  const putPolicyMatch = /^\/policies\/([^/]+)$/.exec(path);
  if (method === 'PUT' && putPolicyMatch) {
    const policyId = putPolicyMatch[1];
    return handleUpdatePolicy(policyId, body as PolicyUpdateApiRequest);
  }

  // DELETE /policies/:id - ポリシー削除
  const deletePolicyMatch = /^\/policies\/([^/]+)$/.exec(path);
  if (method === 'DELETE' && deletePolicyMatch) {
    const policyId = deletePolicyMatch[1];
    return handleDeletePolicy(policyId);
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/policies - ポリシー一覧取得
 * カーソルページネーション対応（モックでは全件返却）
 */
function handleGetPolicies(): Observable<HttpResponse<PolicyListApiResponse>> {
  return createMockResponse({ items: MOCK_POLICIES, nextCursor: null });
}

/**
 * POST /api/v0/tenants/:tenantId/policies - ポリシー作成
 * 201 Createdを返す。実 API と同じく、リクエストの写しではなく作成後の状態
 * （GET /policies/:id と同じ内容）を返し、一覧と詳細にも反映する
 */
function handleCreatePolicy(
  body: PolicyCreateApiRequest,
): Observable<HttpResponse<PolicyDetailApiResponse | { message: string }>> {
  const isTenantAdmin = body.isTenantAdmin ?? false;
  const newPolicy: PolicyDetailApiResponse = {
    publicId: `policy-new-${Date.now()}`,
    name: body.name,
    description: body.description ?? '',
    isTenantAdmin,
    // テナント管理者ポリシーは指定に関係なく全グループ writable 1 件で表される
    permissions: isTenantAdmin
      ? [{ groupId: '*', access: 'writable' }]
      : mergePermissionsByGroup(body.permissions ?? []),
    users: [],
  };
  MOCK_POLICY_DETAILS.push(newPolicy);
  MOCK_POLICIES.push({
    publicId: newPolicy.publicId,
    name: newPolicy.name,
    description: newPolicy.description,
    isTenantAdmin,
    userCount: 0,
    createdAt: new Date().toISOString(),
  });

  return createMockResponse<PolicyDetailApiResponse>(newPolicy, 201);
}

/**
 * 同じグループの指定を 1 件にまとめる（実 API の保存結果と同じ）。
 * どれか 1 つでも writable なら writable。並びはグループが最初に現れた順。
 */
function mergePermissionsByGroup(
  permissions: readonly PermissionInput[],
): Permission[] {
  const accessByGroup = new Map<string, PermissionAccess>();
  for (const p of permissions) {
    accessByGroup.set(
      p.groupId,
      accessByGroup.get(p.groupId) === 'writable' ? 'writable' : p.access,
    );
  }
  return Array.from(accessByGroup, ([groupId, access]) => ({
    groupId,
    access,
  }));
}

/**
 * GET /api/v0/tenants/:tenantId/policies/:id - ポリシー詳細取得
 */
function handleGetPolicyDetail(
  policyId: string,
): Observable<HttpResponse<PolicyDetailApiResponse | { message: string }>> {
  const policy = findPolicyDetailById(policyId);
  if (!policy) {
    return createMockErrorResponse('ポリシーが見つかりません', 404);
  }
  return createMockResponse<PolicyDetailApiResponse>(policy);
}

/**
 * PUT /api/v0/tenants/:tenantId/policies/:id - ポリシー更新
 */
function handleUpdatePolicy(
  policyId: string,
  body: PolicyUpdateApiRequest,
): Observable<HttpResponse<PolicyDetailApiResponse | { message: string }>> {
  const existing = findPolicyDetailById(policyId);
  if (!existing) {
    return createMockErrorResponse('ポリシーが見つかりません', 404);
  }

  const updatedPolicy: PolicyDetailApiResponse = {
    ...existing,
    name: body.name,
    description: body.description,
    isTenantAdmin: body.isTenantAdmin ?? existing.isTenantAdmin,
    permissions: (body.permissions ?? []).map((p) => ({
      groupId: p.groupId,
      access: p.access,
    })),
  };

  return createMockResponse<PolicyDetailApiResponse>(updatedPolicy);
}

/**
 * DELETE /api/v0/tenants/:tenantId/policies/:id - ポリシー削除
 * 204 No Contentを返す
 * isTenantAdmin=trueの場合は403、ユーザー割当中の場合は409を返す
 */
function handleDeletePolicy(
  policyId: string,
): Observable<HttpResponse<null | { message: string }>> {
  const policy = findPolicyDetailById(policyId);
  if (!policy) {
    return createMockErrorResponse('ポリシーが見つかりません', 404);
  }

  // テナント管理者ポリシーは削除不可
  if (policy.isTenantAdmin) {
    return createMockErrorResponse(
      'テナント管理者ポリシーは削除できません',
      403,
    );
  }

  // ユーザー割当中の場合は削除不可
  if ((policy.users ?? []).length > 0) {
    return createMockErrorResponse(
      'ユーザーが割り当てられているため削除できません',
      409,
    );
  }

  return createMockResponse<null>(null, 204);
}
